import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { getCollections } from '@api/apiCalls'
import { analytics } from '@helpers/analytics'
import trackError from '@helpers/trackError'
import { RestProduct } from 'types/product'
import { objectKeys } from '@helpers/utils'
import useOnScreen from '@helpers/hooks/useOnScreen'
import { getProductCategory, productListViewed } from '@helpers/products'
import { getLink, getCurrencyCode } from '@helpers/env'
import { fetchContentfulProductsPromo } from '@api/graphqlContentFul'
import ProductsSlider, {
  ProductSliderProduct
} from '@components/ui/ProductsSlider'
import SectionHeader from '@components/ui/SectionHeader'
import { ButtonLink } from '@components/ui/Button'
import styles from './styles.module.scss'
import { useFit } from '@helpers/hooks/useFit'

type Collection = { items: RestProduct[] }

type Promo = {
  collection?: string
  title: string
  linkUrl: string
  linkText: string
}

const MAX_PRODUCTS_LENGTH = 4

function removeProductTypesWithEmptyItems(
  collections: Record<string, Collection>
) {
  const purgedList: Record<string, Collection> = {}
  objectKeys(collections).forEach(key => {
    const value = collections[key]
    if (value.items?.length > 0) {
      purgedList[key] = value
    }
  })
  return purgedList
}

function getMultiProductTypeProductsList(collectionProducts: Collection[]) {
  if (collectionProducts?.length === MAX_PRODUCTS_LENGTH) {
    return collectionProducts.map(product => product.items[0])
  }

  const productsList: RestProduct[] = []
  const remainingProducts: RestProduct[] = []
  collectionProducts.forEach(product => {
    const [first, ...rest] = product.items
    productsList.push(first)
    remainingProducts.push(...rest)
  })
  return [...productsList, ...remainingProducts]
}

type ProductsPromoProps = {
  pageHandle: string
  productType?: string
  onLinkClick: (args: Collection | string | undefined) => void
  firstTimeUser?: boolean
}
function ProductsPromo({
  pageHandle,
  productType,
  onLinkClick
}: ProductsPromoProps) {
  const { query } = useRouter()
  const { savedFit } = useFit()
  const [{ promo, products }, setPromoState] = useState<{
    promo: Promo | null
    products: ProductSliderProduct[] | null
  }>({
    promo: null,
    products: null
  })
  const elementRef = useRef<HTMLDivElement>(null)
  const hasSeenProductsPromoRef = useRef<boolean>()
  const isOnScreen = useOnScreen(elementRef)

  useEffect(() => {
    if (isOnScreen && products && !hasSeenProductsPromoRef.current) {
      const eventProducts = products.map((product, index) => ({
        quantity: 1,
        name: product.title,
        color: product.byline,
        price: parseFloat(product.price),
        position: index + 1,
        url: product.handle,
        image_url: product.image,
        brand: 'SPOKE'
      }))
      productListViewed({
        context: 'Products Promo',
        listId: promo?.collection,
        products: eventProducts,
        savedFit
      })
      hasSeenProductsPromoRef.current = true
    }
  }, [isOnScreen, products, promo])

  useEffect(() => {
    const fetchProductsPromo = async () => {
      try {
        const { productsPromo } =
          (await fetchContentfulProductsPromo({
            productType,
            pageHandle,
            preview: query.preview === 'true'
          })) || {}

        if (productsPromo?.items.length) {
          const productsPromoData = productsPromo.items[0]
          const collectionData = await getCollections(
            productsPromoData.collection
          )
          const purgedCollections = removeProductTypesWithEmptyItems(
            collectionData.collections
          )
          const collectionProducts = Object.values(purgedCollections).slice(
            0,
            MAX_PRODUCTS_LENGTH
          )

          const isSingleProductType = collectionProducts.length === 1
          const productsList = isSingleProductType
            ? collectionProducts[0]?.items
            : getMultiProductTypeProductsList(collectionProducts)

          const parsedProductsData = productsList
            .slice(0, MAX_PRODUCTS_LENGTH)
            .map(product => {
              return {
                handle: product.handle,
                id: product.id,
                tags: product.tags,
                title: product.product_type,
                byline: product.title,
                price: product.price,
                fullPrice: product.compare_at_price,
                image: product?.images && product?.images[1].src,
                imageHover: product?.images && product?.images[0].src,
                options: product.options
              } as ProductSliderProduct
            })

          setPromoState({
            promo: {
              title: productsPromoData.title,
              linkText: productsPromoData.linkText,
              linkUrl: productsPromoData.linkUrl,
              collection: productsPromoData.collection
            },
            products: parsedProductsData
          })
        }
      } catch (error) {
        trackError(new Error('Products promo failed'), {
          error: error as Error
        })
      }
    }
    if (pageHandle || productType) {
      fetchProductsPromo()
    }
  }, [pageHandle, productType, query.preview])

  const handleOnLinkClick = () => {
    onLinkClick(promo?.collection)
  }

  const handleOnProductLinkClick = (product: ProductSliderProduct) => {
    analytics('Product Clicked', {
      list_id: promo?.collection,
      context: 'Products Promo',
      category: getProductCategory(product.options),
      quantity: 1,
      name: product.title,
      color: product.byline,
      price: parseFloat(product.price),
      url: getLink(`/products/${product.handle}`),
      image_url: product.image,
      brand: 'SPOKE',
      currency: getCurrencyCode()
    })
  }

  if (!promo) {
    return null
  }

  return (
    <section
      className={`${styles.ProductsPromo} grid`}
      id="ProductsPromo"
      ref={elementRef}
    >
      <div className={`${styles.ProductsPromo__container}`}>
        {!!promo?.title && (
          <SectionHeader
            className={styles.ProductsPromo__sectionHeader}
            title={promo?.title}
          />
        )}
        {products && (
          <ProductsSlider
            onProductLinkClick={handleOnProductLinkClick}
            products={products}
          />
        )}
        {promo?.linkUrl && promo?.linkText && (
          <ButtonLink
            onClick={handleOnLinkClick}
            href={promo?.linkUrl}
            variant="secondary"
            size="small"
            classes={styles.ProductsPromo__button}
          >
            {promo?.linkText}
          </ButtonLink>
        )}
      </div>
    </section>
  )
}

export default ProductsPromo
